import React, { PropsWithChildren, cloneElement, isValidElement, ReactElement } from 'react';
import classNames from 'classnames';
import { BadgeKind, BadgeColor, BadgeSize, BadgeShape } from './types';
import {
  badge_base_css,
  badge_icon_size_css,
  badge_size_css,
  getBadgeStyleByProps,
  rectangle_badge_size_css,
} from './styles';
import { IconProps } from '../icons/generated';

export interface BadgeProps {
  className?: string;
  /** @default 'outline' */
  kind?: BadgeKind;
  /** @default 'pill' */
  shape?: BadgeShape;
  /** @default 'small' */
  size?: BadgeSize;
  /** @default 'gray' */
  color?: BadgeColor;
  /**
   * 라벨 좌측 영역에 렌더링 될 아이콘 요소
   *  - Icon 컴포넌트 color props가 있더라도 BadgeColor에 따라 currentColor가 지정됩니다.
   *  - Icon 컴포넌트 size props가 없으면 기본으로 8이 지정됩니다.
   */
  startIcon?: ReactElement<IconProps>;
  /**
   * 라벨 우측 영역에 렌더링 될 아이콘 요소
   *  - Icon 컴포넌트 color props가 있더라도 BadgeColor에 따라 currentColor가 지정됩니다.
   *  - Icon 컴포넌트 size props가 없으면 기본으로 8이 지정됩니다.
   */
  endIcon?: ReactElement<IconProps>;
}

export const Badge = ({
  className,
  kind = 'outline',
  shape = 'pill',
  size = 'small',
  color = 'gray',
  startIcon,
  endIcon,
  children,
}: PropsWithChildren<BadgeProps>) => {
  const _start_icon = isValidElement(startIcon)
    ? cloneElement(startIcon, { color: 'currentColor', size: startIcon.props.size ?? badge_icon_size_css[size] })
    : null;
  const _end_icon = isValidElement(endIcon)
    ? cloneElement(endIcon, { color: 'currentColor', size: endIcon.props.size ?? badge_icon_size_css[size] })
    : null;

  return (
    <span
      className={classNames('pds-badge', className)}
      css={[
        badge_base_css,
        badge_size_css[size],
        ...(shape === 'rectangle' ? [rectangle_badge_size_css[size]] : []),
        getBadgeStyleByProps(kind, color),
      ]}
    >
      {_start_icon}
      {children}
      {_end_icon}
    </span>
  );
};
